if (!customElements.get('cart-drawer-recommendations')) {
  class CartDrawerRecommendations extends HTMLElement {
    #abortController = null;
    #showTimeout = null;
    #originalParent = null;
    #currentAnimation = null;
    #fetchPromise = null;
    #cacheKey = null;
    #cachedDesktop = null;
    #cachedMobile = null;
    #mediaQuery = null;
    #onMediaChange = null;

    static #DURATION = 300;
    static #EASING = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
    static #HIDE_TRANSFORM = 'translateX(calc(var(--transform-logical-flip) * 100%))';

    connectedCallback() {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;
      this.cartDrawer = document.getElementById('cart-drawer');

      if (!this.cartDrawer) {
        return;
      }

      this.#mediaQuery = window.matchMedia('(min-width: 700px)');
      this.#onMediaChange = () => this.#onViewportChange();

      this.cartDrawer.addEventListener('dialog:before-show', () => this.#onCartBeforeShow(), { signal });
      this.cartDrawer.addEventListener('dialog:after-show', () => this.#onCartAfterShow(), { signal });
      this.cartDrawer.addEventListener('dialog:before-hide', () => this.#onCartBeforeHide(), { signal });
      this.cartDrawer.addEventListener('dialog:after-hide', () => this.#onCartAfterHide(), { signal });
      this.cartDrawer.addEventListener('cart-drawer:refreshed', () => this.#onCartRefreshed(), { signal });
      this.#mediaQuery.addEventListener('change', this.#onMediaChange);
      signal.addEventListener('abort', () => this.#mediaQuery?.removeEventListener('change', this.#onMediaChange));
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#clearShowTimeout();
      this.#cancelAnimation();
      this.#fetchPromise = null;
    }

    #isDesktop() {
      return this.#mediaQuery?.matches ?? window.matchMedia('(min-width: 700px)').matches;
    }

    #getCacheKey() {
      return [
        this.dataset.recommendationProductId,
        this.dataset.sectionId,
        this.dataset.recommendationsIntent || 'related',
        this.dataset.recommendationsLimit || 6
      ].join(':');
    }

    #invalidateCache() {
      this.#cacheKey = null;
      this.#cachedDesktop = null;
      this.#cachedMobile = null;
    }

    #onCartBeforeShow() {
      if (!this.#isDesktop()) {
        return;
      }

      if (this.cartDrawer.parentElement === document.body && this.parentElement !== document.body) {
        this.#originalParent = this.parentElement;
        document.body.insertBefore(this, this.cartDrawer.nextSibling);
      } else if (
        this.cartDrawer.parentElement === document.body &&
        this.parentElement === document.body &&
        this.compareDocumentPosition(this.cartDrawer) & Node.DOCUMENT_POSITION_PRECEDING
      ) {
        document.body.insertBefore(this, this.cartDrawer.nextSibling);
      }
    }

    #onCartAfterShow() {
      this.#syncViewport();
    }

    #onCartBeforeHide() {
      this.#clearShowTimeout();

      if (!this.hasAttribute('hidden')) {
        this.#animateOut();
      }
    }

    #onCartAfterHide() {
      this.#cancelAnimation();
      this.setAttribute('hidden', '');
      this.style.removeProperty('transform');
      this.style.removeProperty('pointer-events');

      if (this.parentElement === document.body && this.#originalParent) {
        const restoreTarget = this.#originalParent.querySelector('cart-drawer') || this.cartDrawer;

        if (restoreTarget && restoreTarget.parentElement === this.#originalParent) {
          this.#originalParent.insertBefore(this, restoreTarget);
        } else {
          this.#originalParent.appendChild(this);
        }
      }
    }

    #onCartRefreshed() {
      if (!this.cartDrawer?.open) {
        return;
      }

      this.#invalidateCache();

      if (this.#isDesktop()) {
        const wasVisible = !this.hasAttribute('hidden');
        this.#clearShowTimeout();
        this.#cancelAnimation();
        this.#syncDesktop({ force: true, wasVisible });
      } else {
        this.#hideDesktopPanel();
        this.#syncMobile({ force: true });
      }
    }

    #onViewportChange() {
      if (!this.cartDrawer?.open) {
        return;
      }

      this.#syncViewport({ preferDesktopVisible: this.#isDesktop() });
    }

    #syncViewport({ preferDesktopVisible = false } = {}) {
      if (!this.cartDrawer?.open) {
        return Promise.resolve();
      }

      if (this.#isDesktop()) {
        const wasVisible = preferDesktopVisible || !this.hasAttribute('hidden');
        return this.#syncDesktop({ wasVisible });
      }

      this.#hideDesktopPanel();
      return this.#syncMobile();
    }

    #syncDesktop({ force = false, wasVisible = null } = {}) {
      if (!this.cartDrawer?.open || !this.#isDesktop()) {
        return Promise.resolve();
      }

      const shouldStayVisible = wasVisible ?? !this.hasAttribute('hidden');

      if (!force && this.#hasContent()) {
        if (shouldStayVisible) {
          this.removeAttribute('hidden');
          this.style.pointerEvents = 'auto';
          this.style.transform = 'translateX(0)';
        }

        return Promise.resolve();
      }

      return this.#fetchMarkup({ force }).then(({ desktop }) => {
        if (!this.cartDrawer?.open || !this.#isDesktop()) {
          return;
        }

        this.#applyDesktopMarkup(desktop);

        if (!this.#hasContent()) {
          this.#hideDesktopPanel();
          return;
        }

        if (shouldStayVisible) {
          this.removeAttribute('hidden');
          this.style.pointerEvents = 'auto';
          this.style.transform = 'translateX(0)';
        } else if (this.hasAttribute('hidden')) {
          this.#scheduleShow();
        }
      });
    }

    #syncMobile({ force = false } = {}) {
      if (!this.cartDrawer?.open || this.#isDesktop()) {
        return Promise.resolve();
      }

      const content = this.#getMobileContentElement();

      if (!content) {
        return Promise.resolve();
      }

      if (!force && this.#hasMobileContent()) {
        return Promise.resolve();
      }

      if (!this.dataset.recommendationProductId) {
        return Promise.resolve();
      }

      return this.#fetchMarkup({ force }).then(({ mobile }) => {
        if (!this.cartDrawer?.open || this.#isDesktop()) {
          return;
        }

        this.#applyMobileMarkup(mobile);
      });
    }

    #fetchMarkup({ force = false } = {}) {
      const cacheKey = this.#getCacheKey();
      const productId = this.dataset.recommendationProductId;
      const sectionId = this.dataset.sectionId;

      if (!productId || !sectionId) {
        return Promise.resolve({ desktop: null, mobile: null });
      }

      if (!force && cacheKey === this.#cacheKey && (this.#cachedDesktop || this.#cachedMobile)) {
        return Promise.resolve({
          desktop: this.#cachedDesktop,
          mobile: this.#cachedMobile
        });
      }

      if (this.#fetchPromise) {
        return this.#fetchPromise;
      }

      const intent = this.dataset.recommendationsIntent || 'related';
      const limit = this.dataset.recommendationsLimit || 6;
      const url = `${window.Shopify?.routes?.root || '/'}recommendations/products?product_id=${productId}&limit=${limit}&section_id=${sectionId}&intent=${intent}`;

      this.#fetchPromise = fetch(url)
        .then((response) => {
          if (!response.ok || !this.isConnected) {
            return null;
          }

          return response.text();
        })
        .then((html) => {
          if (!html || !this.isConnected) {
            return { desktop: null, mobile: null };
          }

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const desktopSource = doc.querySelector('cart-drawer-recommendations');
          const mobileSource = doc.querySelector('cart-drawer .cart-drawer__recommendations-content');

          this.#cacheKey = cacheKey;
          this.#cachedDesktop = desktopSource?.querySelector('.cart-drawer-recommendation-card')
            ? document.importNode(desktopSource, true)
            : null;
          this.#cachedMobile = mobileSource?.querySelector('.cart-drawer-recommendation-card')
            ? document.importNode(mobileSource, true)
            : null;

          return {
            desktop: this.#cachedDesktop,
            mobile: this.#cachedMobile
          };
        })
        .catch(() => ({ desktop: null, mobile: null }))
        .finally(() => {
          this.#fetchPromise = null;
        });

      return this.#fetchPromise;
    }

    #applyDesktopMarkup(markup) {
      if (!markup?.querySelector('.cart-drawer-recommendation-card')) {
        this.replaceChildren();
        return;
      }

      this.replaceChildren(...document.importNode(markup, true).childNodes);
    }

    #applyMobileMarkup(markup) {
      const content = this.#getMobileContentElement();

      if (!content) {
        return;
      }

      if (!markup?.querySelector('.cart-drawer-recommendation-card')) {
        content.replaceChildren();
        return;
      }

      content.replaceChildren(...document.importNode(markup, true).childNodes);
    }

    #getMobileContentElement() {
      return this.cartDrawer?.querySelector('.cart-drawer__recommendations-content');
    }

    #hideDesktopPanel() {
      this.#clearShowTimeout();
      this.#cancelAnimation();

      if (!this.hasAttribute('hidden')) {
        this.setAttribute('hidden', '');
      }

      this.style.removeProperty('transform');
      this.style.removeProperty('pointer-events');
    }

    #scheduleShow() {
      this.#clearShowTimeout();

      this.#showTimeout = window.setTimeout(() => {
        this.#showTimeout = null;

        if (this.cartDrawer?.open && this.#isDesktop() && this.#hasContent() && this.hasAttribute('hidden')) {
          this.#animateIn();
        }
      }, 500);
    }

    #animateIn() {
      if (!this.#isDesktop()) {
        return;
      }

      this.#cancelAnimation();
      this.removeAttribute('hidden');
      this.style.pointerEvents = 'auto';
      this.style.transform = CartDrawerRecommendations.#HIDE_TRANSFORM;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.isConnected || !this.cartDrawer?.open) {
            return;
          }

          this.#currentAnimation = this.animate(
            [
              { transform: CartDrawerRecommendations.#HIDE_TRANSFORM },
              { transform: 'translateX(0)' }
            ],
            {
              duration: CartDrawerRecommendations.#DURATION,
              easing: CartDrawerRecommendations.#EASING,
              fill: 'forwards'
            }
          );

          this.#currentAnimation.addEventListener('finish', () => {
            this.#currentAnimation = null;
          }, { once: true });
        });
      });
    }

    #animateOut() {
      this.#cancelAnimation();
      this.style.pointerEvents = 'none';

      this.#currentAnimation = this.animate(
        [
          { transform: 'translateX(0)' },
          { transform: CartDrawerRecommendations.#HIDE_TRANSFORM }
        ],
        {
          duration: CartDrawerRecommendations.#DURATION,
          easing: CartDrawerRecommendations.#EASING,
          fill: 'forwards'
        }
      );

      return this.#currentAnimation.finished.then(() => {
        if (!this.isConnected) {
          return;
        }

        this.#currentAnimation = null;
        this.setAttribute('hidden', '');
        this.style.removeProperty('transform');
        this.style.removeProperty('pointer-events');
      });
    }

    #hasContent() {
      return Boolean(this.querySelector('.cart-drawer-recommendation-card'));
    }

    #hasMobileContent() {
      return Boolean(this.cartDrawer?.querySelector('.cart-drawer__recommendations .cart-drawer-recommendation-card'));
    }

    #clearShowTimeout() {
      if (this.#showTimeout != null) {
        window.clearTimeout(this.#showTimeout);
        this.#showTimeout = null;
      }
    }

    #cancelAnimation() {
      if (this.#currentAnimation) {
        this.#currentAnimation.cancel();
        this.#currentAnimation = null;
      }
    }
  }

  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
